import {
    createTheme,
    StyledEngineProvider,
    ThemeOptions,
    ThemeProvider,
} from '@mui/material/styles';
import { Language } from '../../lib/Localized';
import { ReactNode, useEffect, useMemo } from 'react';
import { deDE, enUS, frFR, itIT, Localization } from '@mui/material/locale';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Settings } from 'luxon';

interface MaterialThemeProviderProps {
    theme: ThemeOptions;
    language: Language;
    children: ReactNode;
}

export function MaterialThemeProvider({
    children,
    theme,
    language,
}: MaterialThemeProviderProps) {
    const localizedMuiTheme = useMemo(() => {
        return createTheme(theme, languageToMuiLocale(language));
    }, [theme, language]);

    useEffect(() => {
        Settings.defaultLocale = language;
    }, [language]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={localizedMuiTheme}>
                <LocalizationProvider
                    adapterLocale={languageToAdapterLocale(language)}
                    dateAdapter={AdapterLuxon}
                >
                    {children}
                </LocalizationProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

function languageToMuiLocale(language: Language): Localization {
    switch (language) {
        case Language.DE:
            return {
                components: {
                    ...deDE.components,
                    MuiAutocomplete: deCH.components?.MuiAutocomplete,
                    MuiAlert: deCH.components?.MuiAlert,
                },
            };
        case Language.FR:
            return frFR;
        case Language.IT:
            return itIT;
        case Language.EN:
            return enUS;
    }
}

function languageToAdapterLocale(language: Language): string {
    switch (language) {
        case Language.EN:
            return 'en-CH';
        default:
            return language;
    }
}

// We want to overwrite the component texts using 'ß'
const deCH: Localization = {
    components: {
        MuiAutocomplete: {
            defaultProps: {
                clearText: 'Leeren',
                closeText: 'Schliessen',
                loadingText: 'Wird geladen…',
                noOptionsText: 'Keine Optionen',
                openText: 'Öffnen',
            },
        },
        MuiAlert: {
            defaultProps: {
                closeText: 'Schliessen',
            },
        },
    },
};
